import { useCallback } from "react";
import { Button } from "@material-tailwind/react";
import { t } from "i18next";
import { toast } from "react-hot-toast";
import { useFileUploader } from "../../hooks/useFileUploader";

interface ImageUploaderProps {
  workorderId: string;
  initialFiles: EasyTech.Photo[];
  type: "BEFORE" | "AFTER";
  buttonText: string;
  onFileAdded?: (file: EasyTech.Photo) => void;
  uploadType: "photos" | "signatures";
  contactId?: string;
  firstname?: string;
  lastname?: string;
  function?: string;
  id?: string;
}

export const ImageUploader = ({
  workorderId,
  type,
  buttonText,
  onFileAdded,
  uploadType,
  contactId,
  firstname,
  lastname,
  function: signFunction,
  id,
}: ImageUploaderProps) => {
  const { loadingType, handleUpload } = useFileUploader({
    workorderId,
    uploadType,
    contactId,
    firstname,
    lastname,
    function: signFunction,
  });

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        try {
          const fileSizeMB = file.size / (1024 * 1024);
          const maxSizeMB = 10; // Taille maximale avant compression

          if (fileSizeMB > maxSizeMB && file.type.startsWith("image/")) {
            toast.error(
              t("workorder:files:errors:fileTooLarge", { maxSize: maxSizeMB }),
              { duration: 4000 }
            );
          }

          const newFile = await handleUpload(file, type);
          if (onFileAdded) onFileAdded(newFile as EasyTech.Photo);

          // Réinitialiser complètement l'input file pour permettre la sélection du même fichier
          const inputId = id || `${uploadType}-upload-${type}`;
          const inputElement = document.getElementById(inputId) as HTMLInputElement;
          if (inputElement) {
            inputElement.value = "";
            // Force la réinitialisation du cache du navigateur
            inputElement.type = "";
            inputElement.type = "file";
          }
        } catch (error) {
          console.error(
            `Erreur lors de l'upload ${
              uploadType === "signatures" ? "de la signature" : "de la photo"
            }:`,
            error
          );

          // Message d'erreur spécifique
          if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error(t("workorder:files:errors:uploadFailed"));
          }
        }
      }
    },
    [handleUpload, type, onFileAdded, uploadType, id]
  );

  return (
    <div className="relative">
      <input
        type="file"
        className="hidden"
        accept={uploadType === "signatures" ? "image/*,.pdf" : "image/*"}
        onChange={handleFileSelect}
        id={id || `${uploadType}-upload-${type}`}
      />
      <Button
        variant="filled"
        className="bg-blue-500 normal-case font-thin"
        disabled={loadingType === type}
        onClick={() =>
          document.getElementById(id || `${uploadType}-upload-${type}`)?.click()
        }
      >
        
        {loadingType === type ? t("Uploading...") : buttonText}
      </Button>
    </div>
  );
};
